import type { ReactEventHandler } from 'react';
import { useState } from 'react';
import type {
  FormElement,
  FormElementConfigW,
  FormElementToInputType,
} from './Types';

// When values are valid.
interface ValidState<T> {
  values: T;
  errors: object;
}

// When errors occurs.
interface ErrorState<T> {
  values: object;
  errors: {
    [k in keyof T]: string[];
  };
}

// Form state hook.
export function useFormWithTransformation<T extends object>(
  initalvalues: T,
  config: FormElementConfigW<T>[],
  callback: (data: T) => Promise<void>
) {
  const [values, setValues] = useState<T>(initalvalues);
  const [validationState, setValidationState] = useState<
    ValidState<T> | ErrorState<T>
  >({ values: initalvalues, errors: {} }); // inital values never validated.
  const [hasSubmitted, setHasSubmitted] = useState(false); // If the user submit at least once.
  const [status, setStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');

 const validate = (values: T) => {
  const isEmpty = Object.values(values).length === 0;

  if (isEmpty) {
    return config.reduce((acc, conf) => {
      conf.fields.forEach((field: keyof T) => {
        acc[field] = ["Ce champ ne peut pas être vide"];
      });
      return acc;
    }, {} as Record<keyof T, string[]>)
  }

  return config.reduce((acc, conf) => {
    const errors = (conf.validator as ((i: T) => string | undefined)[])
      .map((f) => f(values))
      .filter((v): v is string => Boolean(v));

    conf.fields.forEach((field: keyof T) => {
      acc[field] = errors;
    });

    return acc;
  }, {} as Record<keyof T, string[]>);
};


  const validator = (values: T) => {
    const errors = validate(values);
    // Delete all fields without errors from acc.
    Object.keys(errors).forEach((key) => {
      if (errors[key as keyof T].length === 0) {
        delete errors[key as keyof T];
      }
    });

    if (errors) {
      setValidationState({
        values: [],
        errors,
      });
      return {
        values: [],
        errors,
      } as ErrorState<T>;
    } else {
      setValidationState({
        values,
        errors: {},
      });
      return {
        values,
        errors: {},
      } as ValidState<T>;
    }
  };

  /**
   * Get errors for each key.
   */
  function getErrors<k extends keyof T>(key: k): string[] {
    if (validationState === undefined) {
      return [];
    }
    if (
      Object.keys(validationState.errors).includes(key as unknown as string)
    ) {
      return (validationState.errors as Record<keyof T, string[]>)[key];
    }
    return [];
  }

  /**
   * Store input values for any number of fields.
   */
  function setMultipleValues<T extends FormElement>(
    index: number,
    value: FormElementToInputType<T>
  ) {
    const itemConfig = config[index];
    if (!itemConfig) {
      throw new Error('Config out of bounds');
    }

    const transformed = itemConfig.transformator
      ? itemConfig.transformator(value)
      : ({ [itemConfig.fields[0]!]: value } as unknown as Partial<T>);

    const newValues = { ...values, ...transformed };

    setValues(newValues);

    if (hasSubmitted) {
      const newValidationState = validator(newValues);
      setValidationState(newValidationState);
    }
  }

  /**
   * Retrieves input values for a single field.
   */
  function watch<U extends keyof T, V extends T[U]>(key: U): V {
    return values[key] as V;
  }

  // Retrieves input values for all fields.
  function watchAll(): T {
    return values;
  }

  /**
   * Get callback execution status.
   */
  function watchStatus() {
    return status;
  }

  /**
   * Restore values and validation state to default.
   */
  function reset() {
    setValues(initalvalues);
    setHasSubmitted(false);
    setValidationState({ values: initalvalues, errors: {} });
  }

  /**
   * Manage form submission.
   */
  const onSubmit: ReactEventHandler = (e) : boolean => {
    setHasSubmitted(true);
    e.preventDefault();
    const newValidationState = validator(values);

    // Manage case of submission without entering anything.
    setValidationState(newValidationState);

    // Proceed only if there isn't any validation error.
    if (
      newValidationState !== undefined &&
      newValidationState.errors !== undefined &&
      Object.keys(newValidationState.errors).length === 0
    ) {
      setStatus('loading');
      callback(values)
        .then(() => {
          reset();
          setStatus('success');
        })
        .then(() => setStatus('idle'));
    }
      // unstantenly returns errors. 
      const hasErrors = Object.keys(newValidationState.errors).length > 0;
      return hasErrors;
  };

  return {
    watch,
    watchAll,
    onSubmit,
    reset,
    getErrors,
    setMultipleValues,
    hasSubmitted,
    watchStatus,
  };
}
